import {
  CompositeNavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { styled, SxProp } from 'dripsy';
import { Observer, observer } from 'mobx-react-lite';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

import { useAsyncInit, useNavOptionsSet } from '@hooks';
import {
  BaseText,
  Button,
  ButtonStyleEnum,
  CheckBox,
  ListView,
  Separator,
} from '@components';
import { useSupportModel } from '@models/Support/SupportModel';
import {
  MainNavigatorRoutePropType,
  MainNavigatorNavigationPropType,
  HubNavigatorRoutePropType,
  HubNavigatorNavigationPropType,
  MusicNavigatorRoutePropType,
  MusicNavigatorNavigationPropType,
} from '@navigation';
import { ToastService } from '@services';
import { parseApiError } from '@utils';

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

function contentContainerStyle(insets: EdgeInsets): SxProp {
  return (theme) => ({
    flexGrow: 1,
    px: '$6',
    pt: '$6',
    pb: theme.space.$10 + insets.bottom,
  });
}

const listFooterComponentStyle: SxProp = {
  flex: 1,
  justifyContent: 'flex-end',
};

const Title = styled(BaseText)({
  variant: 'text.xlarge',
});

type RoutePropType =
  | MainNavigatorRoutePropType<'Report'>
  | HubNavigatorRoutePropType<'Report'>
  | MusicNavigatorRoutePropType<'Report'>;
type NavigationPropType = CompositeNavigationProp<
  CompositeNavigationProp<
    MainNavigatorNavigationPropType<'Report'>,
    HubNavigatorNavigationPropType<'Report'>
  >,
  MusicNavigatorNavigationPropType<'Report'>
>;

export const ReportScreen = observer(() => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const route = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const support = useSupportModel();

  useNavOptionsSet({ title: t('report.title') });

  useAsyncInit(async () => {
    try {
      await support.report.fetchReasonTypes.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  });

  async function send() {
    try {
      await route.params.item.sendReport.run(
        support.report.selectedReasonTypeIds,
      );

      ToastService.showSuccess(t('report.successMessage'));

      nav.goBack();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  return (
    <Container>
      <ListView
        contentContainerSx={contentContainerStyle(insets)}
        ListFooterComponentSx={listFooterComponentStyle}
        data={support.report.reasonTypes}
        keyExtractor={(i) => i.reasonTypeId.toString()}
        isLoading={support.report.fetchReasonTypes.inProgress}
        ItemSeparatorComponent={() => <Separator size="$7" />}
        ListHeaderComponent={() => (
          <Fragment>
            <Title>{t('report.reasons')}</Title>
            <Separator size="$7" />
          </Fragment>
        )}
        renderItem={({ item }) => (
          <Observer>
            {() => (
              <CheckBox
                value={item.isSelected}
                text={item.title}
                onValueChange={item.toggleSelect}
              />
            )}
          </Observer>
        )}
        ListFooterComponent={() => (
          <Observer>
            {() => (
              <Fragment>
                <Button
                  disabled={!support.report.hasSelectedReasons}
                  onPress={send}
                  isLoading={route.params.item.sendReport.inProgress}
                >
                  {t('report.report')}
                </Button>
                <Separator size="$5" />
                <Button
                  onPress={nav.goBack}
                  buttonStyle={ButtonStyleEnum.Ghost}
                >
                  {t('report.cancel')}
                </Button>
              </Fragment>
            )}
          </Observer>
        )}
      />
    </Container>
  );
});
